import { Intersection } from "../Intersection.js";
import { ORIENTATIONS } from "../../../constants/intxConstants.js";
import { errDiv } from "../../../util/mathHelpers.ts";

// const DEBUG = false;

/** Partial Cloverleaf Type A Interchange computational class. Extends the Intersection parent class */
export class PartialCloverleafA extends Intersection {
  /**
   * Constructor for the NewIntxTemplate class.
   * @param {string} name - Name of the intersection
   * @param {Object} volumes - Object mapping direction strings (eastbound, westbound, northbound, southbound) to  {@link Volume} objects.
   * @param {Object} globalParams - Object containing the required global parameters for an intersection.
   * @param {string} freewayDirection - Optional string identifier for the freeway direction, default = DIR_NS
   */
  constructor(
    name,
    volumes,
    globalParams,
    freewayDirection = ORIENTATIONS.NORTH_SOUTH
  ) {
    super(name, volumes, globalParams);
    this.freewayDirection = freewayDirection;

    this.LaneConfig = PartialCloverleafA.getZoneDefaultInputs();
  }

  /**
   * Function to get the DEFAULT inputs available for the interchange. This function is designed to facilitate the
   * integration of the engine into a user interface.
   *
   * A traditional diamond interchange has two zones (Z3, Z4) and is dependent of on the specified freeway direction.
   *
   * @return {Object} Object representation of default inputs
   */
  static getZoneDefaultInputs() {
    return {
      Z3: {
        Cross1: {
          T: 1,
        },
        Cross2: {
          T: 1,
        },
        Fwy2: {
          LT: 1,
          RT: 1,
        },
      },
      Z4: {
        Cross2: {
          T: 1,
        },
        Cross1: {
          T: 1,
        },
        Fwy1: {
          LT: 1,
          RT: 1,
        },
      },
    };
  }

  static getLaneConfigLabelsMapping(orientation) {
    const isEW = orientation === ORIENTATIONS.EAST_WEST;
    return {
      Z3: this.getInterchangeLabelsMappingWithMajorDir(isEW),
      Z4: this.getInterchangeLabelsMappingWithMajorDir(isEW),
    };
  }

  setLaneConfigInputs(laneConfigInputs) {
    this.LaneConfig = laneConfigInputs;
  }

  // Implements the computeVCAnalysis function of the Intersection parent class.
  _runCriticalMovementAnalysis() {
    const volumes = this.generateInterchangeVolumes(this.freewayDirection);

    const Z3CLV =
      errDiv(
        volumes.Cross2.LT + volumes.Cross2.T,
        this.LaneConfig.Z3.Cross2.T
      ) + errDiv(volumes.Fwy2.LT, this.LaneConfig.Z3.Fwy2.LT * this.LTAF);
    const Z3VC = errDiv(Z3CLV, this.CLV_Limit_2);

    const Z4CLV =
      errDiv(
        volumes.Cross1.LT + volumes.Cross1.T,
        this.LaneConfig.Z4.Cross1.T
      ) + errDiv(volumes.Fwy1.LT, this.LaneConfig.Z4.Fwy1.LT * this.LTAF);
    const Z4VC = errDiv(Z4CLV, this.CLV_Limit_2);

    // Assign results for each zone
    this._resultsByZone = {
      Z3: {
        VC: Z3VC,
        CLV: Z3CLV,
      },
      Z4: {
        VC: Z4VC,
        CLV: Z4CLV,
      },
    };
  }
}
